export type ProposalState =
  | { status: "active" }
  | { status: "cancelled" }
  | { status: "created"; batchId: string; runsRequired?: number }
  | { status: "completed"; batchId?: string; runsRequired?: number };

const PROPOSAL_STORAGE_PREFIX = "assistant-asset-batch-proposal";

export function shouldPollCreatedBatchStatus(status: string | null | undefined) {
  return !status || status === "draft" || status === "scheduled" || status === "generating";
}

export function getProposalStorageKey(brandId: string, proposalId: string) {
  return `${PROPOSAL_STORAGE_PREFIX}:${brandId}:${proposalId}`;
}

export function loadProposalState(key: string): ProposalState {
  if (typeof window === "undefined") {
    return { status: "active" };
  }

  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) {
      return { status: "active" };
    }

    const parsed = JSON.parse(stored) as Partial<ProposalState> & { runsRequired?: unknown };
    if (parsed.status === "cancelled") {
      return { status: "cancelled" };
    }
    const runsRequired =
      typeof parsed.runsRequired === "number" &&
      Number.isInteger(parsed.runsRequired) &&
      parsed.runsRequired >= 0
        ? parsed.runsRequired
        : undefined;
    if (parsed.status === "created" && typeof parsed.batchId === "string") {
      return { status: "created", batchId: parsed.batchId, runsRequired };
    }
    if (parsed.status === "completed") {
      return {
        status: "completed",
        ...(typeof parsed.batchId === "string" ? { batchId: parsed.batchId } : {}),
        runsRequired,
      };
    }
  } catch {
    return { status: "active" };
  }

  return { status: "active" };
}

export function saveProposalState(
  key: string,
  state: Exclude<ProposalState, { status: "active" }>,
) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage is best-effort; the server mutation remains the source of truth for creation.
  }
}
